import { HexBoard } from '../game/hex/HexBoard';
import { HexTile } from '../game/hex/HexTile';
import { HexCoord, PixelCoord, HexLayout, TileType } from '../game/hex/HexTypes';
import { RouteResult, MarbleSequence, HiddenMarble } from '../game/RouteSearcherV4';
import { PlayMode, GreenChipPosition } from '../game/PlayMode';
import { AnimationInfo, MiniGameMode } from '../game/MiniGameMode';
import { TwitterShare } from '../utils/TwitterShare';

/**
 * ヘクサゴンマップの描画クラス
 */
export class HexRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private layout: HexLayout;
  private currentRoute: RouteResult | null = null;
  private board: HexBoard | null = null;
  private playMode: PlayMode | null = null;
  private miniGameMode: MiniGameMode | null = null;
  private animations: AnimationInfo[] = [];
  private penaltyAnimationStart: number = 0;
  private penaltyAnimationDuration: number = 2000; // 2秒間表示
  private showTitleScreen: boolean = false;
  private isMultiplayerMode: boolean = false;
  private multiplayerScores: Map<string, { name: string, score: number }> = new Map();
  
  // 色設定
  private colors = {
    ring0: {
      fill: '#FFD700',      // ゴールド（中央）
      stroke: '#DAA520',
      highlight: '#FFFF00',
      selected: '#FFE55C'
    },
    ring1: {
      fill: '#87CEEB',      // スカイブルー（第1リング）
      stroke: '#4682B4',
      highlight: '#ADD8E6',
      selected: '#B0E0E6'
    },
    ring2: {
      fill: '#98FB98',      // ペールグリーン（第2リング）
      stroke: '#3CB371',
      highlight: '#90EE90',
      selected: '#98FB98'
    },
    text: '#333333',
    border: '#000000'
  };
  
  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    
    // レイアウト設定
    this.layout = {
      size: 40,
      spacing: 2,
      origin: { x: canvas.width / 2, y: canvas.height / 2 }
    };
    
    this.setupCanvas();
  }
  
  /**
   * Canvas設定
   */
  private setupCanvas(): void {
    // Canvas サイズを設定（パターン表示分を追加）
    const canvasWidth = 500;
    const canvasHeight = 550; // パターン表示分の高さを追加
    
    // Retina display対応
    const dpr = window.devicePixelRatio || 1;
    
    // 物理サイズを設定
    this.canvas.width = canvasWidth * dpr;
    this.canvas.height = canvasHeight * dpr;
    
    // CSS表示サイズを設定（aspect-ratioで制御するため削除）
    // this.canvas.style.width = `${canvasWidth}px`;
    // this.canvas.style.height = `${canvasHeight}px`;
    
    // スケーリングを適用
    this.ctx.scale(dpr, dpr);

    // 中央を原点に設定（パターン表示のために下にオフセット）
    // +60pxのオフセットでパターン表示との重なりを回避
    this.layout.origin = { x: canvasWidth / 2, y: canvasHeight / 2 + 60 };
  }
  
  /**
   * Cube座標をピクセル座標に変換
   */
  private hexToPixel(coord: HexCoord): PixelCoord {
    const size = this.layout.size;
    const x = size * (3/2 * coord.x);
    const y = size * (Math.sqrt(3)/2 * coord.x + Math.sqrt(3) * coord.z);
    
    return {
      x: this.layout.origin.x + x,
      y: this.layout.origin.y + y
    };
  }
  
  /**
   * アニメーションを設定
   */
  public setAnimations(animations: AnimationInfo[]): void {
    this.animations = animations;
  }
  
  /**
   * ミニゲームモードを設定
   */
  public setMiniGameMode(miniGameMode: MiniGameMode | null): void {
    this.miniGameMode = miniGameMode;
  }
  
  /**
   * タイトル画面表示を設定
   */
  public setShowTitleScreen(show: boolean): void {
    this.showTitleScreen = show;
  }
  
  /**
   * マルチプレイヤーモードを設定
   */
  public setMultiplayerMode(enabled: boolean): void {
    this.isMultiplayerMode = enabled;
  }
  
  /**
   * マルチプレイヤーのスコアを更新
   */
  public updateMultiplayerScores(scores: Map<string, { name: string, score: number }>): void {
    this.multiplayerScores = scores;
  }
  
  /**
   * ピクセル座標をCube座標に変換（クリック判定用）
   */
  public pixelToHex(pixel: PixelCoord): HexCoord {
    const size = this.layout.size;
    const x = (pixel.x - this.layout.origin.x) / size;
    const y = (pixel.y - this.layout.origin.y) / size;
    
    const q = (2/3) * x;
    const r = (-1/3 * x + Math.sqrt(3)/3 * y);
    const s = -q - r;
    
    // 最も近い整数座標に丸める
    let rx = Math.round(q);
    let ry = Math.round(r);
    let rz = Math.round(s);
    
    const xDiff = Math.abs(rx - q);
    const yDiff = Math.abs(ry - r);
    const zDiff = Math.abs(rz - s);
    
    // Cube座標の制約を維持
    if (xDiff > yDiff && xDiff > zDiff) {
      rx = -ry - rz;
    } else if (yDiff > zDiff) {
      ry = -rx - rz;
    } else {
      rz = -rx - ry;
    }
    
    return { x: rx, y: ry, z: rz };
  }
  
  /**
   * 正六角形の頂点を計算
   */
  private getHexCorners(center: PixelCoord): PixelCoord[] {
    const corners: PixelCoord[] = [];
    const size = this.layout.size - this.layout.spacing;
    
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      corners.push({
        x: center.x + size * Math.cos(angle),
        y: center.y + size * Math.sin(angle)
      });
    }
    
    return corners;
  }
  
  /**
   * タイルを描画
   */
  private drawTile(tile: HexTile): void {
    const center = this.hexToPixel(tile.coords);
    const corners = this.getHexCorners(center);
    
    // パスを作成
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    
    // 背景色（白ベース）
    if (tile.isSelected()) {
      this.ctx.fillStyle = '#FFE55C';
    } else if (tile.isHighlighted()) {
      this.ctx.fillStyle = '#FFFF99';
    } else {
      this.ctx.fillStyle = '#FFFFFF';
    }
    this.ctx.fill();
    
    // 枠線
    this.ctx.strokeStyle = '#999999';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
    
    // タイルタイプに応じた丸を描画
    this.drawTileContent(tile, center);
    
    // 新規配置マーカーを描画
    if (tile.isNewlyPlaced()) {
      this.drawNewlyPlacedMarker(tile, center);
    }
  }
  
  /**
   * タイルの内容（青丸、赤丸）を描画
   */
  private drawTileContent(tile: HexTile, center: PixelCoord): void {
    const tileType = tile.getBaseTileType();
    
    // 中央タイルの場合は特別な表示
    if (tile.coords.x === 0 && tile.coords.y === 0 && tile.coords.z === 0) {
      this.drawCenterTileArrows(center);
      return;
    }
    
    if (tileType === 'EMPTY') {
      return; // 空タイルは何も描画しない
    }
    
    // 旗頭タイルの特別な表示
    const flagshipTile = this.board?.getFlagshipTile();
    if (flagshipTile && tile === flagshipTile) {
      this.drawFlagshipContent(center);
      return;
    }
    
    // プレイモードでアニメーション中の場合
    if (this.playMode && this.playMode.isAnimating(tile.coords)) {
      const texts = this.playMode.getAnimationText(tile.coords);
      this.drawAnimationText(center, texts);
      return;
    }
    
    // 描画設定
    const circleRadius = 8;
    
    // 統一的な位置計算を使用（calculateAllMarblePositionsと同じロジック）
    const marbles = this.getMarblesArray(tile);
    const positions = this.layoutMarbles(center, marbles.length);
    
    // 各ビー玉を描画
    for (let i = 0; i < marbles.length; i++) {
      const color = marbles[i] === 'BLUE' ? '#4169E1' : '#DC143C';
      this.drawCircle(positions[i], color, circleRadius);
    }
    
    // 星マークを描画
    if (tile.hasStar()) {
      this.drawStar(center);
    }
  }
  
  /**
   * 星マークを描画
   */
  private drawStar(center: PixelCoord): void {
    const size = 12;
    const innerRadius = size * 0.4;
    const outerRadius = size;
    const points = 5;
    
    this.ctx.save();
    this.ctx.translate(center.x, center.y - 25);
    
    this.ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const angle = (Math.PI / points) * i - Math.PI / 2;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      
      if (i === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }
    }
    this.ctx.closePath();
    
    // 星の塗りつぶしと縁取り
    this.ctx.fillStyle = '#FFD700';
    this.ctx.fill();
    this.ctx.strokeStyle = '#FFA500';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
    
    this.ctx.restore();
  }
  
  /**
   * 指定個数の丸を描画
   * @returns ビー玉の座標配列
   */
  private drawCircles(center: PixelCoord, color: string, count: number, tile?: HexCoord): PixelCoord[] {
    const circleRadius = 8;
    const spacing = 20;
    const positions: PixelCoord[] = [];
    
    if (count === 1) {
      // 1個の場合は中央に
      const pos = center;
      positions.push(pos);
      this.drawCircle(pos, color, circleRadius);
      
      // プレイモードで緑チップがある場合
      if (this.playMode && tile) {
        const hasGreenChip = this.playMode.getGreenChips().some(
          gc => gc.tile.x === tile.x && gc.tile.y === tile.y && gc.tile.z === tile.z && gc.marbleIndex === 0
        );
        if (hasGreenChip) {
          this.drawGreenChip(pos);
        }
      }
    } else if (count === 2) {
      // 2個の場合は横並びに
      const pos1 = { x: center.x - spacing/2, y: center.y };
      const pos2 = { x: center.x + spacing/2, y: center.y };
      positions.push(pos1, pos2);
      
      this.drawCircle(pos1, color, circleRadius);
      this.drawCircle(pos2, color, circleRadius);
      
      // プレイモードで緑チップがある場合
      if (this.playMode && tile) {
        const greenChips = this.playMode.getGreenChips();
        greenChips.forEach(gc => {
          if (gc.tile.x === tile.x && gc.tile.y === tile.y && gc.tile.z === tile.z) {
            if (gc.marbleIndex === 0) {
              this.drawGreenChip(pos1);
            } else if (gc.marbleIndex === 1) {
              this.drawGreenChip(pos2);
            }
          }
        });
      }
    }
    
    return positions;
  }
  
  /**
   * 緑チップを描画
   */
  private drawGreenChip(center: PixelCoord): void {
    const chipRadius = 15;
    
    // 外側の緑の円
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, chipRadius, 0, Math.PI * 2);
    this.ctx.fillStyle = '#4CAF50';  // より明るい緑
    this.ctx.fill();
    
    // 濃い緑の縁取り
    this.ctx.strokeStyle = '#1B5E20';
    this.ctx.lineWidth = 3;
    this.ctx.stroke();
    
    // 中央に白いハイライト
    this.ctx.beginPath();
    this.ctx.arc(center.x - 3, center.y - 3, 4, 0, Math.PI * 2);
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    this.ctx.fill();
  }
  
  /**
   * アニメーションテキストを描画
   */
  private drawAnimationText(center: PixelCoord, texts: string[]): void {
    this.ctx.save();
    
    // 背景を描画
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    this.ctx.strokeStyle = '#333';
    this.ctx.lineWidth = 2;
    
    const padding = 15;
    const fontSize = 24;
    this.ctx.font = `bold ${fontSize}px sans-serif`;
    
    // テキストのサイズを計算
    const maxWidth = Math.max(...texts.map(t => this.ctx.measureText(t).width));
    const totalHeight = texts.length * fontSize + (texts.length - 1) * 5;
    
    // 背景の四角形を描画
    this.ctx.beginPath();
    this.ctx.roundRect(
      center.x - maxWidth/2 - padding,
      center.y - totalHeight/2 - padding,
      maxWidth + padding * 2,
      totalHeight + padding * 2,
      5
    );
    this.ctx.fill();
    this.ctx.stroke();
    
    // テキストを描画
    this.ctx.fillStyle = '#333';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    
    texts.forEach((text, i) => {
      const y = center.y - totalHeight/2 + fontSize/2 + i * (fontSize + 5);
      this.ctx.fillText(text, center.x, y);
    });
    
    this.ctx.restore();
  }
  
  /**
   * 単一の丸を描画
   */
  private drawCircle(center: PixelCoord, color: string, radius: number): void {
    // 丸を描画
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = color;
    this.ctx.fill();
    this.ctx.strokeStyle = '#333333';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }
  
  /**
   * 中央タイルの対辺を結ぶ矢印を描画
   */
  private drawCenterTileArrows(center: PixelCoord): void {
    const arrowLength = 25;
    const arrowHeadSize = 6;
    
    // 3組の対辺を結ぶ両端矢印を描画（六角形の辺に対して垂直方向）
    const angles = [
      Math.PI / 6,     // 30度（上右辺と下左辺を結ぶ）
      Math.PI / 2,     // 90度（上辺と下辺を結ぶ）
      5 * Math.PI / 6  // 150度（上左辺と下右辺を結ぶ）
    ];
    
    this.ctx.strokeStyle = '#666666';
    this.ctx.lineWidth = 2;
    this.ctx.fillStyle = '#666666';
    
    angles.forEach(angle => {
      // 線を描画
      const x1 = center.x + Math.cos(angle) * arrowLength;
      const y1 = center.y + Math.sin(angle) * arrowLength;
      const x2 = center.x - Math.cos(angle) * arrowLength;
      const y2 = center.y - Math.sin(angle) * arrowLength;
      
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x2, y2);
      this.ctx.stroke();
      
      // 両端に矢印を描画（向きを修正）
      this.drawArrowHead(x1, y1, angle, arrowHeadSize);
      this.drawArrowHead(x2, y2, angle + Math.PI, arrowHeadSize);
    });
  }
  
  /**
   * 矢印の先端を描画
   */
  private drawArrowHead(x: number, y: number, angle: number, size: number): void {
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    this.ctx.lineTo(
      x + Math.cos(angle + 2.5) * size,
      y + Math.sin(angle + 2.5) * size
    );
    this.ctx.lineTo(
      x + Math.cos(angle - 2.5) * size,
      y + Math.sin(angle - 2.5) * size
    );
    this.ctx.closePath();
    this.ctx.fill();
  }
  
  /**
   * 新規配置マーカー（黄色い丸）を描画
   */
  private drawNewlyPlacedMarker(tile: HexTile, tileCenter: PixelCoord): void {
    // 中央(0,0,0)から見たタイルの方向を計算
    const centerCoord = { x: 0, y: 0, z: 0 };
    const direction = Math.atan2(
      tileCenter.y - this.layout.origin.y,
      tileCenter.x - this.layout.origin.x
    );
    
    // タイルの外側（中央から遠い側）にマーカーを配置
    const markerDistance = this.layout.size + 15;
    const markerX = tileCenter.x + Math.cos(direction) * markerDistance;
    const markerY = tileCenter.y + Math.sin(direction) * markerDistance;
    
    // 黄色い丸を描画
    this.ctx.beginPath();
    this.ctx.arc(markerX, markerY, 6, 0, Math.PI * 2);
    this.ctx.fillStyle = '#FFD700';
    this.ctx.fill();
    this.ctx.strokeStyle = '#DAA520';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
  }
  
  /**
   * 旗頭タイルの内容（🔵/🔴）を描画
   */
  private drawFlagshipContent(center: PixelCoord): void {
    // テキストで🔵/🔴を描画
    this.ctx.font = 'bold 24px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    
    // 青丸と赤丸を横並びで表示
    const spacing = 15;
    this.ctx.fillText('🔵', center.x - spacing, center.y);
    this.ctx.fillText('🔴', center.x + spacing, center.y);
    
    // または斜線で区切る表示
    this.ctx.font = '16px sans-serif';
    this.ctx.fillStyle = '#666666';
    this.ctx.fillText('/', center.x, center.y);
  }
  
  /**
   * 旗頭タイルの赤い縁取りを描画
   */
  private drawFlagshipBorder(tile: HexTile): void {
    const center = this.hexToPixel(tile.coords);
    const corners = this.getHexCorners(center);
    
    // 赤い縁取りのパスを作成
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    
    // 赤い太い枠線を描画
    this.ctx.strokeStyle = '#FF0000';
    this.ctx.lineWidth = 4;
    this.ctx.stroke();
    
    // 内側にもう一本細い線を描画（二重線効果）
    const innerCorners = this.getHexCorners(center);
    const shrinkFactor = 0.9;
    for (let i = 0; i < innerCorners.length; i++) {
      innerCorners[i].x = center.x + (innerCorners[i].x - center.x) * shrinkFactor;
      innerCorners[i].y = center.y + (innerCorners[i].y - center.y) * shrinkFactor;
    }
    
    this.ctx.beginPath();
    this.ctx.moveTo(innerCorners[0].x, innerCorners[0].y);
    for (let i = 1; i < 6; i++) {
      this.ctx.lineTo(innerCorners[i].x, innerCorners[i].y);
    }
    this.ctx.closePath();
    
    this.ctx.strokeStyle = '#CC0000';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
  }
  
  /**
   * ボード全体を描画
   */
  public render(board: HexBoard): void {
    // ボード参照を保存
    this.board = board;
    
    // キャンバスをクリア
    this.ctx.clearRect(0, 0, 550, 550);
    
    // 背景
    this.ctx.fillStyle = '#f0f0f0';
    this.ctx.fillRect(0, 0, 500, 550);
    
    // 旗頭タイルを取得
    const flagshipTile = board.getFlagshipTile();
    
    // リングごとに描画（外側から内側へ）
    for (let ring = 2; ring >= 0; ring--) {
      const tiles = board.getTilesByRing(ring);
      tiles.forEach(tile => {
        this.drawTile(tile);
        // 旗頭タイルには赤い縁取りを追加
        if (flagshipTile && tile === flagshipTile) {
          this.drawFlagshipBorder(tile);
        }
      });
    }
    
    // ルートが設定されていれば描画
    if (this.currentRoute) {
      this.drawRoute();
      this.drawHiddenMarbles();
    }
    
    // プレイモードの緑チップを描画
    if (this.playMode && this.playMode.isEnabled()) {
      this.drawGreenChips();
      // デバッグ: ビー玉位置を表示（必要に応じてコメントアウト）
      // this.drawMarbleDebugPositions();
    }
    
    // 拡大アニメーションを描画（playModeに依存しない）
    this.drawExpandingAnimations();
    
    // ミニゲームモードのパターン進捗を描画
    if (this.miniGameMode && this.miniGameMode.isEnabled()) {
      this.drawPatternProgress();
    }
    
    // タイトル画面またはタイマー/スコアを描画
    if (this.miniGameMode && !this.miniGameMode.isEnabled() && !this.miniGameMode.isGameComplete()) {
      this.drawTitleScreen();
    } else if (this.miniGameMode && this.miniGameMode.isEnabled()) {
      if (this.isMultiplayerMode) {
        // マルチプレイヤーモードではスコアボードを表示
        this.drawMultiplayerScores();
      } else {
        // シングルプレイヤーモードではタイマーを表示
        this.drawTimer();
      }
    }
    
    // 答えを見るボタンを描画（シングルプレイヤーモードのゲーム中のみ）
    if (!this.isMultiplayerMode && this.miniGameMode && this.miniGameMode.isEnabled() && !this.miniGameMode.isGameComplete()) {
      this.drawHintButton();
    }
    
    // ゲームクリア表示を描画
    if (this.miniGameMode && this.miniGameMode.isGameComplete()) {
      this.drawGameComplete();
    }
    
    // ペナルティアニメーションを描画
    this.drawPenaltyAnimation();
  }
  
  /**
   * ルートを設定
   */
  public setRoute(route: RouteResult | null): void {
    this.currentRoute = route;
  }
  
  /**
   * ルートの矢印を設定（ミニゲーム用）
   */
  public setRouteArrows(route: any[]): void {
    // RouteResultの形式に変換
    if (route && route.length > 0) {
      this.currentRoute = {
        success: true,
        route: route,
        marbles: [],
        hiddenMarbles: []
      } as RouteResult;
    } else {
      this.currentRoute = null;
    }
  }
  
  /**
   * プレイモードを設定
   */
  public setPlayMode(playMode: PlayMode | null): void {
    this.playMode = playMode;
  }
  
  /**
   * ルートを描画
   */
  private drawRoute(): void {
    if (!this.currentRoute || !this.currentRoute.success) return;
    
    const route = this.currentRoute.route;
    const marbles = this.currentRoute.marbles || [];
    const hiddenMarbles = this.currentRoute.hiddenMarbles || [];
    
    // marblesが空の場合は、routeからタイル間の矢印を描画
    if (marbles.length === 0 && route.length > 0) {
      // console.warn('No marble sequences found, drawing simple route arrows'); // 毎フレーム出力されるためコメントアウト
      for (let i = 0; i < route.length - 1; i++) {
        const from = this.hexToPixel(route[i].coords);
        const to = this.hexToPixel(route[i + 1].coords);
        this.drawRouteArrow(from, to, 'UNKNOWN');
      }
      return;
    }
    
    // 矢印を描画
    let marbleIndex = 0;
    
    for (let i = 0; i < marbles.length; i++) {
      const marbleSeq = marbles[i];
      const tileCenter = this.hexToPixel(marbleSeq.tile.coords);
      
      // このタイルで使った丸の位置を計算（隠された丸を考慮）
      const positions = this.calculateUsedMarblePositions(marbleSeq.tile, marbleSeq.sequence, hiddenMarbles);
      
      // 丸同士を矢印で結ぶ
      for (let j = 0; j < positions.length - 1; j++) {
        this.drawRouteArrow(positions[j], positions[j + 1], marbleSeq.sequence[j]);
      }
      
      // 次のタイルへの矢印
      if (i < marbles.length - 1) {
        const nextTile = marbles[i + 1].tile;
        const nextCenter = this.hexToPixel(nextTile.coords);
        const nextPositions = this.calculateUsedMarblePositions(nextTile, marbles[i + 1].sequence, hiddenMarbles);
        
        if (positions.length > 0 && nextPositions.length > 0) {
          // タイル間の矢印
          this.drawRouteArrow(
            positions[positions.length - 1],
            nextPositions[0],
            marbleSeq.sequence[marbleSeq.sequence.length - 1]
          );
        }
      }
    }
  }
  
  /**
   * 使用した丸の位置を計算（隠された丸を考慮）
   */
  private calculateUsedMarblePositions(tile: HexTile, sequence: string[], hiddenMarbles: HiddenMarble[]): PixelCoord[] {
    const center = this.hexToPixel(tile.coords);
    const positions: PixelCoord[] = [];
    const tileType = tile.getBaseTileType();  // 星マークを除いた基本タイプを取得
    const spacing = 20;
    
    // 旗頭タイルの場合は特別処理
    const flagshipTile = this.board?.getFlagshipTile();
    if (flagshipTile && tile === flagshipTile) {
      // 旗頭タイルでは選択された色（赤または青）の位置を返す
      if (sequence.length > 0) {
        const color = sequence[0]; // 旗頭タイルは1つの色として扱われる
        if (color === 'BLUE') {
          positions.push({ x: center.x - 15, y: center.y }); // 青丸の位置
        } else if (color === 'RED') {
          positions.push({ x: center.x + 15, y: center.y }); // 赤丸の位置
        }
      }
      return positions;
    }
    
    // タイル内の全ての丸の位置と色を取得
    const allPositions = this.calculateAllMarblePositions(tile);
    const originalMarbles = this.getOriginalMarbles(tileType);
    
    // 隠された丸のインデックスを取得
    const hiddenIndices = hiddenMarbles
      .filter(h => h.tile.id === tile.id)
      .map(h => h.marbleIndex);
    
    // 利用可能な丸とその位置のマッピングを作成
    const availableMarbles: { color: string; position: PixelCoord; index: number }[] = [];
    for (let i = 0; i < originalMarbles.length; i++) {
      if (!hiddenIndices.includes(i)) {
        availableMarbles.push({
          color: originalMarbles[i],
          position: allPositions[i],
          index: i
        });
      }
    }
    
    // sequenceに基づいて使用した丸の位置を決定
    const used = new Set<number>();
    for (const marbleColor of sequence) {
      // 同じ色の丸で、まだ使用していないものを探す
      for (const marble of availableMarbles) {
        if (marble.color === marbleColor && !used.has(marble.index)) {
          positions.push(marble.position);
          used.add(marble.index);
          break;
        }
      }
    }
    
    return positions;
  }
  
  /**
   * タイルの元の丸の配列を取得
   */
  private getOriginalMarbles(tileType: string): string[] {
    // 星マーク付きタイルも考慮
    const baseType = tileType.replace('_STAR', '');
    switch (baseType) {
      case 'BLUE_1': return ['BLUE'];
      case 'RED_1': return ['RED'];
      case 'BLUE_2': return ['BLUE', 'BLUE'];
      case 'RED_2': return ['RED', 'RED'];
      case 'BLUE_1_RED_1': return ['BLUE', 'RED'];
      case 'BLUE_2_RED_1': return ['BLUE', 'BLUE', 'RED'];
      case 'BLUE_1_RED_2': return ['BLUE', 'RED', 'RED'];
      default: return [];
    }
  }
  
  /**
   * タイル内の丸の位置を計算（旧関数、互換性のため残す）
   */
  private calculateMarblePositions(tile: HexTile, sequence: string[]): PixelCoord[] {
    const center = this.hexToPixel(tile.coords);
    const positions: PixelCoord[] = [];
    const tileType = tile.getBaseTileType();
    
    // タイルタイプに応じた丸の配置を取得
    const spacing = 20;
    
    switch (tileType) {
      case 'BLUE_1':
      case 'RED_1':
        positions.push(center);
        break;
        
      case 'BLUE_2':
      case 'RED_2':
        positions.push({ x: center.x - spacing/2, y: center.y });
        positions.push({ x: center.x + spacing/2, y: center.y });
        break;
        
      case 'BLUE_1_RED_1':
        // 左が青、右が赤
        if (sequence[0] === 'BLUE') {
          positions.push({ x: center.x - spacing/2, y: center.y });
          if (sequence.length > 1) {
            positions.push({ x: center.x + spacing/2, y: center.y });
          }
        } else {
          positions.push({ x: center.x + spacing/2, y: center.y });
          if (sequence.length > 1) {
            positions.push({ x: center.x - spacing/2, y: center.y });
          }
        }
        break;
        
      case 'BLUE_2_RED_1':
        // 正三角形配置
        const triangleRadius = spacing * 0.6;
        // 使用順序に応じて位置を返す
        for (const marble of sequence) {
          if (marble === 'RED') {
            positions.push({ x: center.x, y: center.y - triangleRadius });
          } else if (positions.filter(p => 
            Math.abs(p.x - (center.x - triangleRadius * 0.866)) < 1 &&
            Math.abs(p.y - (center.y + triangleRadius * 0.5)) < 1
          ).length === 0) {
            positions.push({ x: center.x - triangleRadius * 0.866, y: center.y + triangleRadius * 0.5 });
          } else {
            positions.push({ x: center.x + triangleRadius * 0.866, y: center.y + triangleRadius * 0.5 });
          }
        }
        break;
        
      case 'BLUE_1_RED_2':
        // 正三角形配置
        const triangleRadius2 = spacing * 0.6;
        for (const marble of sequence) {
          if (marble === 'BLUE') {
            positions.push({ x: center.x, y: center.y - triangleRadius2 });
          } else if (positions.filter(p => 
            Math.abs(p.x - (center.x - triangleRadius2 * 0.866)) < 1 &&
            Math.abs(p.y - (center.y + triangleRadius2 * 0.5)) < 1
          ).length === 0) {
            positions.push({ x: center.x - triangleRadius2 * 0.866, y: center.y + triangleRadius2 * 0.5 });
          } else {
            positions.push({ x: center.x + triangleRadius2 * 0.866, y: center.y + triangleRadius2 * 0.5 });
          }
        }
        break;
    }
    
    return positions;
  }
  
  /**
   * ルートの矢印を描画
   */
  private drawRouteArrow(from: PixelCoord, to: PixelCoord, marbleColor: string): void {
    const color = '#FFD700'; // 黄色に変更
    
    // 矢印の線
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    this.ctx.globalAlpha = 0.7;
    
    this.ctx.beginPath();
    this.ctx.moveTo(from.x, from.y);
    this.ctx.lineTo(to.x, to.y);
    this.ctx.stroke();
    
    // 矢印の先端（サイズを2倍に）
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const arrowSize = 16; // 8から16に変更（2倍）
    
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(to.x, to.y);
    this.ctx.lineTo(
      to.x - arrowSize * Math.cos(angle - Math.PI / 6),
      to.y - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.lineTo(
      to.x - arrowSize * Math.cos(angle + Math.PI / 6),
      to.y - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.closePath();
    this.ctx.fill();
    
    this.ctx.globalAlpha = 1;
  }
  
  /**
   * 隠された丸を描画
   */
  private drawHiddenMarbles(): void {
    if (!this.currentRoute || !this.currentRoute.hiddenMarbles) return;
    
    for (const hidden of this.currentRoute.hiddenMarbles) {
      const center = this.hexToPixel(hidden.tile.coords);
      const positions = this.calculateAllMarblePositions(hidden.tile);
      
      if (hidden.marbleIndex < positions.length) {
        const pos = positions[hidden.marbleIndex];
        
        // 緑の×印を描画
        this.ctx.strokeStyle = '#00AA00';
        this.ctx.lineWidth = 3;
        const size = 6;
        
        this.ctx.beginPath();
        this.ctx.moveTo(pos.x - size, pos.y - size);
        this.ctx.lineTo(pos.x + size, pos.y + size);
        this.ctx.moveTo(pos.x + size, pos.y - size);
        this.ctx.lineTo(pos.x - size, pos.y + size);
        this.ctx.stroke();
      }
    }
  }
  
  /**
   * タイルのビー玉配列（色列）を取得
   */
  private getMarblesArray(tile: HexTile): ("RED"|"BLUE")[] {
    const tileType = tile.getBaseTileType();  // 星マークを除いた基本タイプを取得
    
    switch (tileType) {
      case 'BLUE_1':        return ["BLUE"];
      case 'RED_1':         return ["RED"];
      case 'BLUE_2':        return ["BLUE", "BLUE"];
      case 'RED_2':         return ["RED", "RED"];
      case 'BLUE_1_RED_1':  return ["BLUE", "RED"];
      case 'BLUE_2_RED_1':  return ["BLUE", "BLUE", "RED"];
      case 'BLUE_1_RED_2':  return ["BLUE", "RED", "RED"];
      case 'EMPTY':
      default:              
        return [];
    }
  }
  
  /**
   * n個のビー玉を見やすく配置する汎用レイアウト
   */
  private layoutMarbles(center: PixelCoord, n: number): PixelCoord[] {
    const spacing = 20; // 元の実装と同じ間隔
    
    switch (n) {
      case 0: 
        return [];
      case 1: 
        return [{ x: center.x, y: center.y }];
      case 2: 
        return [
          { x: center.x - spacing/2, y: center.y },
          { x: center.x + spacing/2, y: center.y }
        ];
      case 3: // 正三角形配置（元の実装と同じ）
        const triangleRadius = spacing * 0.6; // 12
        return [
          { x: center.x - triangleRadius * 0.866, y: center.y + triangleRadius * 0.5 }, // 左下
          { x: center.x + triangleRadius * 0.866, y: center.y + triangleRadius * 0.5 }, // 右下
          { x: center.x, y: center.y - triangleRadius } // 上
        ];
      default:
        // 想定外（将来拡張用）。円周配置
        const out: PixelCoord[] = [];
        for (let i = 0; i < n; i++) {
          const ang = (Math.PI * 2 * i) / n;
          out.push({ 
            x: center.x + spacing * Math.cos(ang), 
            y: center.y + spacing * Math.sin(ang) 
          });
        }
        return out;
    }
  }
  
  /**
   * タイル内の全ての丸の位置を計算（隠し丸表示用）
   */
  private calculateAllMarblePositions(tile: HexTile): PixelCoord[] {
    // 中央タイル（矢印表示）の場合は空配列を返す
    if (tile.coords.x === 0 && tile.coords.y === 0 && tile.coords.z === 0) {
      return [];
    }
    
    // 旗頭タイル判定を一時的に無効化（デバッグ用）
    // const flagshipTile = this.board?.getFlagshipTile();
    // if (flagshipTile && tile === flagshipTile) {
    //   console.log(`calculateAllMarblePositions: Flagship tile ${tile.id} (Ring ${tile.ring}), returning empty`);
    //   return [];
    // }
    
    const center = this.hexToPixel(tile.coords);
    
    // ビー玉の個数だけに依存して汎用配置
    const marbles = this.getMarblesArray(tile);
    const positions = this.layoutMarbles(center, marbles.length);
    return positions;
  }
  
  /**
   * クリック座標からタイルを取得
   */
  public getTileFromCoordinates(x: number, y: number, board: HexBoard): HexTile | null {
    // マップの中心がずれているので、原点を基準に反転する
    // 原点: (250, 300) - layout.origin
    // Canvas座標系: Y軸下向きが正
    // Hex座標系: Y軸上向きが正
    
    // 原点からの相対位置を計算
    const relativeY = y - this.layout.origin.y;
    
    // 原点を基準に反転
    const invertedRelativeY = -relativeY;
    
    // 反転後の絶対座標
    const invertedY = this.layout.origin.y + invertedRelativeY;
    
    const hexCoord = this.pixelToHex({ x, y: invertedY });
    
    // デバッグ: クリック位置とHex座標を出力
    const tile = board.getTileAt(hexCoord);
    // if (tile && tile.getTileType() !== 'EMPTY') {
    //   console.log(`Click at (${x}, ${y}) -> Origin(${this.layout.origin.x}, ${this.layout.origin.y}) -> InvertedY(${invertedY}) -> Hex(${hexCoord.x}, ${hexCoord.y}, ${hexCoord.z}) -> TileType: ${tile.getTileType()}`);
    // }
    
    return tile;
  }
  
  /**
   * レイアウト設定を更新
   */
  public updateLayout(layout: Partial<HexLayout>): void {
    this.layout = { ...this.layout, ...layout };
  }
  
  /**
   * クリック位置からビー玉インデックスを取得
   */
  public getMarbleIndexFromClick(x: number, y: number, tile: HexTile): number {
    const positions = this.calculateAllMarblePositions(tile);
    const clickRadius = 20; // クリック判定の半径を少し大きくする
    
    // x, yは論理座標として渡されており、positionsも論理座標
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const dist = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));
      if (dist <= clickRadius) {
        return i;
      }
    }
    
    return -1; // ビー玉以外をクリック
  }
  
  /**
   * 緑チップを描画
   */
  private drawGreenChips(): void {
    if (!this.playMode) return;
    
    const greenChips = this.playMode.getGreenChips();
    
    for (const chip of greenChips) {
      const tile = this.board?.getTileByCoords(chip.tile);
      if (!tile) continue;
      
      const positions = this.calculateAllMarblePositions(tile);
      if (chip.marbleIndex < positions.length) {
        const pos = positions[chip.marbleIndex];
        this.drawGreenChip(pos);
      }
    }
  }
  
  /**
   * デバッグ: ビー玉の位置を可視化
   */
  private drawMarbleDebugPositions(): void {
    
    if (!this.board) return;
    
    const tiles = this.board.getAllTiles();
    for (const tile of tiles) {
      if (tile.getTileType() === 'EMPTY') continue;
      
      const positions = this.calculateAllMarblePositions(tile);
      positions.forEach((pos, index) => {
        // 小さな円でクリック判定範囲を表示
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, 20, 0, Math.PI * 2);  // クリック判定と同じサイズに
        this.ctx.strokeStyle = 'rgba(255, 0, 255, 0.3)';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();
        
        // インデックス番号を表示
        this.ctx.fillStyle = 'rgba(255, 0, 255, 0.8)';
        this.ctx.font = '10px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(index.toString(), pos.x, pos.y - 20);
      });
    }
  }
  
  /**
   * 拡大アニメーションを描画
   */
  private drawExpandingAnimations(): void {
    // playModeまたは直接設定されたアニメーションを使用
    const animations = this.animations.length > 0 ? this.animations : 
                      (this.playMode ? this.playMode.getActiveAnimations() : []);
    
    if (animations.length === 0) return;
    
    const now = Date.now();
    
    for (const anim of animations) {
      const elapsed = now - anim.startTime;
      const duration = 667; // 約0.67秒でアニメーション（2000/3）
      
      if (elapsed >= duration) continue;
      
      const progress = elapsed / duration;
      const opacity = 1 - progress; // フェードアウト
      const scale = 1 + progress * 10; // 1から11倍まで拡大
      
      // テキストのサイズと位置を計算
      const fontSize = 30 * scale;
      
      // 半透明で描画
      this.ctx.save();
      this.ctx.globalAlpha = opacity;
      this.ctx.font = `bold ${fontSize}px sans-serif`;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      
      // テキストの色（チム=赤、ドン=青、失敗=グレー、完成=金色）
      if (anim.text === 'チム') {
        this.ctx.fillStyle = '#DC143C';
        this.ctx.strokeStyle = 'white';
      } else if (anim.text === 'ドン') {
        this.ctx.fillStyle = '#1E90FF';
        this.ctx.strokeStyle = 'white';
      } else if (anim.text.includes('失敗')) {
        // 失敗メッセージ（詳細メッセージも含む）
        this.ctx.fillStyle = '#666666';
        this.ctx.strokeStyle = 'white';
        // 長いメッセージの場合はフォントサイズを調整
        if (anim.text.length > 10) {
          const adjustedFontSize = Math.min(fontSize, 20 * scale);
          this.ctx.font = `bold ${adjustedFontSize}px sans-serif`;
        }
      } else if (anim.text === '完成！') {
        this.ctx.fillStyle = '#FFD700';
        this.ctx.strokeStyle = '#FF6347';
      } else {
        this.ctx.fillStyle = '#333333';
        this.ctx.strokeStyle = 'white';
      }
      
      // 縁取り付きで描画
      this.ctx.lineWidth = 3 * scale;
      this.ctx.strokeText(anim.text, anim.x, anim.y);
      this.ctx.fillText(anim.text, anim.x, anim.y);
      
      this.ctx.restore();
    }
  }
  
  /**
   * パターン進捗を描画
   */
  private drawPatternProgress(): void {
    if (!this.miniGameMode) return;

    const pattern = ['チム', 'チム', 'ドン', 'ドン', 'チム', 'ドン', 'ドン'];
    const currentIndex = this.miniGameMode.getCurrentPatternIndex();
    const canvasWidth = 500; // 論理座標でのキャンバス幅

    // 上部に配置
    const y = 40;
    const boxWidth = 50;
    const boxHeight = 35;
    const spacing = 10;
    const totalWidth = pattern.length * boxWidth + (pattern.length - 1) * spacing;
    const startX = (canvasWidth - totalWidth) / 2;

    // 「次: チム」が表示されるかチェック
    const nextText = this.miniGameMode.getNextExpectedText();
    const nextBoxHeight = nextText ? 40 : 0; // 「次: チム」用のスペース

    // 背景パネルを描画（「次: チム」を含む高さ）
    const panelHeight = boxHeight + 40 + nextBoxHeight;
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    this.ctx.strokeStyle = '#333';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.roundRect(startX - 15, y - 25, totalWidth + 30, panelHeight, 10);
    this.ctx.fill();
    this.ctx.stroke();

    // タイトル
    this.ctx.fillStyle = '#333';
    this.ctx.font = 'bold 14px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(`ラウンド ${this.miniGameMode.getCurrentRound()} / ${this.miniGameMode.getMaxRounds()}`, canvasWidth / 2, y - 10);
    
    // パターンのボックスを描画
    pattern.forEach((text, index) => {
      const x = startX + index * (boxWidth + spacing);
      
      // ボックスの状態を決定
      let bgColor = '#f0f0f0';
      let borderColor = '#ccc';
      let textColor = text === 'チム' ? '#DC143C' : '#1E90FF';
      let borderWidth = 2;
      
      if (index < currentIndex) {
        // 完了済み
        bgColor = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        borderColor = '#764ba2';
        textColor = 'white';
      } else if (index === currentIndex) {
        // 現在の項目（アニメーション付き）
        const time = Date.now() / 1000;
        const pulse = Math.sin(time * 3) * 0.5 + 0.5;
        bgColor = `rgba(255, 215, 0, ${0.3 + pulse * 0.4})`;
        borderColor = '#FFD700';
        borderWidth = 3;
      }
      
      // ボックスを描画
      this.ctx.save();
      
      // グラデーション背景の場合
      if (index < currentIndex) {
        const gradient = this.ctx.createLinearGradient(x, y, x + boxWidth, y + boxHeight);
        gradient.addColorStop(0, '#667eea');
        gradient.addColorStop(1, '#764ba2');
        this.ctx.fillStyle = gradient;
      } else {
        this.ctx.fillStyle = bgColor;
      }
      
      this.ctx.strokeStyle = borderColor;
      this.ctx.lineWidth = borderWidth;
      
      this.ctx.beginPath();
      this.ctx.roundRect(x, y, boxWidth, boxHeight, 6);
      this.ctx.fill();
      this.ctx.stroke();
      
      // テキストを描画
      this.ctx.fillStyle = index < currentIndex ? 'white' : textColor;
      this.ctx.font = 'bold 18px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(text, x + boxWidth / 2, y + boxHeight / 2);
      
      this.ctx.restore();
    });
    
    // 次に必要なマーブルを表示
    if (nextText) {
      const nextY = y + boxHeight + 10; // パターンボックスの下に少し余白

      // 背景
      this.ctx.fillStyle = nextText === 'チム' ? '#FFE4E1' : '#E6F3FF';
      this.ctx.strokeStyle = nextText === 'チム' ? '#DC143C' : '#1E90FF';
      this.ctx.lineWidth = 2;

      const nextBoxWidth = 100;
      const nextBoxX = (canvasWidth - nextBoxWidth) / 2;

      this.ctx.beginPath();
      this.ctx.roundRect(nextBoxX, nextY, nextBoxWidth, 30, 15);
      this.ctx.fill();
      this.ctx.stroke();

      // テキスト
      this.ctx.fillStyle = nextText === 'チム' ? '#DC143C' : '#1E90FF';
      this.ctx.font = 'bold 16px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(`次: ${nextText}`, canvasWidth / 2, nextY + 15);
    }
  }
  
  /**
   * タイトル画面を描画
   */
  private drawTitleScreen(): void {
    const canvasWidth = 500;
    const canvasHeight = 550;
    
    // 半透明の黒いオーバーレイ
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // タイトルテキスト（オレンジ色）
    this.ctx.fillStyle = '#FF8C00';  // ダークオレンジ
    this.ctx.font = 'bold 42px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('チムドンミニ', canvasWidth / 2, canvasHeight / 2 - 100);
    
    // サブタイトル
    this.ctx.fillStyle = 'white';
    this.ctx.font = '24px sans-serif';
    this.ctx.fillText('- ソロプレイ版 -', canvasWidth / 2, canvasHeight / 2 - 50);
    
    // ゲーム説明（マージンを増やす）
    this.ctx.font = '18px sans-serif';
    this.ctx.fillStyle = '#FFD700';  // ゴールド色で目立たせる
    this.ctx.fillText('1分を切れたら天才', canvasWidth / 2, canvasHeight / 2 + 10);
    
    // ゲームスタートボタン
    const buttonWidth = 200;
    const buttonHeight = 50;
    const buttonX = (canvasWidth - buttonWidth) / 2;
    const buttonY = canvasHeight / 2 + 60;
    
    // ボタンの背景（グラデーション）
    const gradient = this.ctx.createLinearGradient(buttonX, buttonY, buttonX, buttonY + buttonHeight);
    gradient.addColorStop(0, '#5CB85C');
    gradient.addColorStop(1, '#4CAF50');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    // ボタンの枠線
    this.ctx.strokeStyle = 'white';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    // ボタンのテキスト
    this.ctx.fillStyle = 'white';
    this.ctx.font = 'bold 20px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('ゲームスタート', canvasWidth / 2, buttonY + buttonHeight / 2);
  }
  
  /**
   * 経過時間を描画（ゲーム中のみ）
   */
  private drawTimer(): void {
    if (!this.miniGameMode || !this.miniGameMode.isEnabled() || this.miniGameMode.isGameComplete()) return;
    
    const canvasWidth = 500;
    const canvasHeight = 550;
    const buttonHeight = 40;
    const buttonY = canvasHeight - 60;
    
    // 背景を半透明の黒に（幅を広げる）
    const timerBoxWidth = 280;
    const timerBoxX = (canvasWidth - timerBoxWidth) / 2;
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(timerBoxX, buttonY, timerBoxWidth, buttonHeight);
    
    // 枠線
    this.ctx.strokeStyle = '#FFD700';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(timerBoxX, buttonY, timerBoxWidth, buttonHeight);
    
    // 現在のラウンド時間と合計時間を取得
    const currentTimer = this.miniGameMode.getCurrentRoundTimer();
    const totalTime = this.miniGameMode.getTotalElapsedTime();
    
    // 現在のラウンド時間
    let roundTimeText = '0.0秒';
    if (currentTimer) {
      const elapsed = currentTimer.endTime 
        ? currentTimer.elapsed 
        : Date.now() - currentTimer.startTime;
      const roundSeconds = (elapsed / 1000).toFixed(1);
      roundTimeText = `${roundSeconds}秒`;
    }
    
    // 合計時間
    const totalSecondsFloat = totalTime / 1000;
    const totalMinutes = Math.floor(totalSecondsFloat / 60);
    const totalSeconds = (totalSecondsFloat % 60).toFixed(1);
    const totalTimeText = totalMinutes > 0 ? `${totalMinutes}分${totalSeconds}秒` : `${totalSeconds}秒`;
    
    this.ctx.fillStyle = 'white';
    this.ctx.font = 'bold 14px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    
    // ラウンド時間を左側に表示
    this.ctx.fillText(`R${this.miniGameMode.getCurrentRound()}: ${roundTimeText}`, timerBoxX + timerBoxWidth * 0.25, buttonY + buttonHeight / 2);
    
    // 合計時間を右側に表示
    this.ctx.fillText(`合計: ${totalTimeText}`, timerBoxX + timerBoxWidth * 0.75, buttonY + buttonHeight / 2);
  }
  
  /**
   * ゲームクリア表示を描画
   */
  private drawGameComplete(): void {
    if (!this.miniGameMode) return;
    
    const canvasWidth = 500;
    const canvasHeight = 550;
    
    // 半透明の背景
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // クリアメッセージの背景
    const boxWidth = 400;
    const boxHeight = 330;  // ツイートボタン追加のため高さを増やす
    const boxX = (canvasWidth - boxWidth) / 2;
    const boxY = 50;
    
    this.ctx.fillStyle = 'white';
    this.ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    
    this.ctx.strokeStyle = '#4CAF50';
    this.ctx.lineWidth = 3;
    this.ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
    
    // タイトル
    this.ctx.fillStyle = '#4CAF50';
    this.ctx.font = 'bold 36px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('🎉 ゲームクリア！ 🎉', canvasWidth / 2, boxY + 50);
    
    // 総時間とペナルティ
    const totalTime = this.miniGameMode.getTotalElapsedTime();
    const penaltyTime = this.miniGameMode.getPenaltyTime();
    const totalSecondsFloat = totalTime / 1000;
    const minutes = Math.floor(totalSecondsFloat / 60);
    const seconds = (totalSecondsFloat % 60).toFixed(1);
    
    this.ctx.fillStyle = '#333';
    this.ctx.font = 'bold 24px sans-serif';
    const timeText = minutes > 0 ? `総クリア時間: ${minutes}分${seconds}秒` : `総クリア時間: ${seconds}秒`;
    this.ctx.fillText(timeText, canvasWidth / 2, boxY + 90);
    
    // ペナルティ時間の表示
    if (penaltyTime > 0) {
      const penaltySeconds = penaltyTime / 1000;
      this.ctx.fillStyle = '#f44336';
      this.ctx.font = '16px sans-serif';
      this.ctx.fillText(`(ペナルティ: +${penaltySeconds}秒)`, canvasWidth / 2, boxY + 115);
    }
    
    // 各ラウンドの時間
    const roundStates = this.miniGameMode.getRoundStates();
    this.ctx.font = '16px sans-serif';
    let yOffset = boxY + 145;
    
    // 2列で表示
    for (let i = 0; i < roundStates.length; i++) {
      const state = roundStates[i];
      const roundSeconds = (state.timer.elapsed / 1000).toFixed(1);
      const x = i < 3 ? canvasWidth / 2 - 100 : canvasWidth / 2 + 100;
      const y = yOffset + (i % 3) * 25;
      
      this.ctx.fillStyle = state.success ? '#4CAF50' : '#f44336';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(`R${state.round}: ${roundSeconds}秒`, x, y);
    }
    
    // 「結果をツイート」ボタンを描画
    const tweetButtonWidth = 200;
    const tweetButtonHeight = 40;
    const tweetButtonX = (canvasWidth - tweetButtonWidth) / 2;
    const tweetButtonY = boxY + 220;

    // ツイートボタンの背景（Twitter青色）
    this.ctx.fillStyle = '#1DA1F2';
    this.ctx.fillRect(tweetButtonX, tweetButtonY, tweetButtonWidth, tweetButtonHeight);

    // ツイートボタンのテキスト
    this.ctx.fillStyle = 'white';
    this.ctx.font = 'bold 16px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('🐦 結果をツイート', canvasWidth / 2, tweetButtonY + tweetButtonHeight / 2);

    // 「もう一度プレイする」ボタンを描画
    const buttonWidth = 180;
    const buttonHeight = 40;
    const buttonX = (canvasWidth - buttonWidth) / 2;
    const buttonY = boxY + 270;  // ツイートボタンの下に配置

    // ボタンの背景
    this.ctx.fillStyle = '#4CAF50';
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);

    // ボタンのテキスト
    this.ctx.fillStyle = 'white';
    this.ctx.font = 'bold 18px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('もう一度プレイする', canvasWidth / 2, buttonY + buttonHeight / 2);
  }
  
  /**
   * 答えを見るボタンを描画
   */
  private drawHintButton(): void {
    const canvasWidth = 500;
    const canvasHeight = 550;
    const buttonWidth = 80;
    const buttonHeight = 30;
    const buttonX = 20;  // 左端から20px
    const buttonY = canvasHeight - 50;  // 下から50px
    
    // 控えめな半透明の背景
    this.ctx.fillStyle = 'rgba(100, 100, 100, 0.3)';
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    // 薄い枠線
    this.ctx.strokeStyle = 'rgba(150, 150, 150, 0.5)';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    // 控えめなテキスト
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.font = '12px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('答えを見る', buttonX + buttonWidth / 2, buttonY + buttonHeight / 2);
  }
  
  /**
   * マルチプレイヤーのスコアを描画
   */
  private drawMultiplayerScores(): void {
    const canvasWidth = 500;
    const canvasHeight = 550;
    const scoreAreaY = canvasHeight - 60;
    const scoreAreaHeight = 50;
    
    // 背景
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(0, scoreAreaY, canvasWidth, scoreAreaHeight);
    
    // タイトル
    this.ctx.fillStyle = '#fff';
    this.ctx.font = 'bold 12px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'top';
    this.ctx.fillText('スコアボード', canvasWidth / 2, scoreAreaY + 5);
    
    // プレイヤーのスコア表示
    const players = Array.from(this.multiplayerScores.entries()).slice(0, 4); // 最大4人
    const playerWidth = canvasWidth / Math.max(players.length, 1);
    
    players.forEach(([id, data], index) => {
      const x = playerWidth * index + playerWidth / 2;
      const y = scoreAreaY + 25;
      
      // プレイヤー名（短縮）
      const displayName = data.name.length > 8 ? data.name.substring(0, 6) + '..' : data.name;
      this.ctx.font = '11px sans-serif';
      this.ctx.fillStyle = '#aaa';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(displayName, x, y);
      
      // スコア
      this.ctx.font = 'bold 14px sans-serif';
      this.ctx.fillStyle = '#fff';
      this.ctx.fillText(data.score.toString(), x, y + 15);
    });
    
    // スコアがない場合
    if (players.length === 0) {
      this.ctx.font = '12px sans-serif';
      this.ctx.fillStyle = '#888';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText('プレイヤー待機中...', canvasWidth / 2, scoreAreaY + scoreAreaHeight / 2);
    }
  }
  
  /**
   * ペナルティアニメーションを開始
   */
  public showPenaltyAnimation(): void {
    this.penaltyAnimationStart = Date.now();
  }
  
  /**
   * ペナルティアニメーションを描画
   */
  private drawPenaltyAnimation(): void {
    if (this.penaltyAnimationStart === 0) return;
    
    const now = Date.now();
    const elapsed = now - this.penaltyAnimationStart;
    
    if (elapsed > this.penaltyAnimationDuration) {
      this.penaltyAnimationStart = 0;
      return;
    }
    
    // アニメーションの進行度
    const progress = elapsed / this.penaltyAnimationDuration;
    
    // フェードアウト（最初は不透明、最後は透明）
    const alpha = 1 - progress;
    
    // 答えを見るボタンの位置から上に浮き上がる
    const buttonX = 20 + 40;  // ボタンの中央X座標
    const buttonY = 500;  // ボタンのY座標
    
    // 上方向への移動（0から-80pxまで）
    const floatY = buttonY - (progress * 80);
    
    // テキストのスケール（最初は小さく、最後は大きく）
    const scale = 0.8 + (progress * 0.8);  // 0.8から1.6へ
    
    // 保存して変形を適用
    this.ctx.save();
    this.ctx.translate(buttonX, floatY);
    this.ctx.scale(scale, scale);
    
    // テキストのみ（背景なし）
    this.ctx.fillStyle = `rgba(255, 50, 50, ${alpha})`;
    this.ctx.font = 'bold 20px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('+15秒', 0, 0);
    
    // 復元
    this.ctx.restore();
  }
  
  /**
   * 答えを見るボタンのクリック判定
   */
  public isHintButtonClicked(x: number, y: number): boolean {
    if (!this.miniGameMode || !this.miniGameMode.isEnabled() || this.miniGameMode.isGameComplete()) {
      return false;
    }
    
    const buttonX = 20;
    const buttonY = 500;  // 550 - 50
    const buttonWidth = 80;
    const buttonHeight = 30;
    
    return x >= buttonX && x <= buttonX + buttonWidth &&
           y >= buttonY && y <= buttonY + buttonHeight;
  }
  
  /**
   * ツイートボタンのクリック判定
   */
  public isTweetButtonClicked(x: number, y: number): boolean {
    // ゲーム完了時のみ有効
    if (!this.miniGameMode || !this.miniGameMode.isGameComplete()) {
      return false;
    }

    const canvasWidth = 500;
    const tweetButtonWidth = 200;
    const tweetButtonHeight = 40;
    const tweetButtonX = (canvasWidth - tweetButtonWidth) / 2;
    const tweetButtonY = 50 + 220;  // boxY + 220

    return x >= tweetButtonX && x <= tweetButtonX + tweetButtonWidth &&
           y >= tweetButtonY && y <= tweetButtonY + tweetButtonHeight;
  }

  /**
   * ボタンクリック判定
   */
  public isStartButtonClicked(x: number, y: number): boolean {
    // ゲーム中はクリックできない
    if (this.miniGameMode && this.miniGameMode.isEnabled() && !this.miniGameMode.isGameComplete()) {
      return false;
    }
    
    const canvasWidth = 500;
    const canvasHeight = 550;
    
    // ゲーム完了時は結果モーダル内の「もう一度プレイする」ボタンを判定
    if (this.miniGameMode && this.miniGameMode.isGameComplete()) {
      const buttonWidth = 180;
      const buttonHeight = 40;
      const buttonX = (canvasWidth - buttonWidth) / 2;
      const buttonY = 50 + 270;  // boxY + 270（ツイートボタンの下）

      return x >= buttonX && x <= buttonX + buttonWidth &&
             y >= buttonY && y <= buttonY + buttonHeight;
    }
    
    // ゲーム開始前はタイトル画面の中央のボタンを判定
    const buttonWidth = 200;
    const buttonHeight = 50;
    const buttonX = (canvasWidth - buttonWidth) / 2;
    const buttonY = canvasHeight / 2 + 60;
    
    return x >= buttonX && x <= buttonX + buttonWidth &&
           y >= buttonY && y <= buttonY + buttonHeight;
  }
  
}